/**
 * useAdminRole — Lazy admin role check with session cache
 *
 * Calls get_admin_role() RPC only on first use, then caches the result.
 * For 99.9% of users (non-admin), this hook is never invoked → zero overhead.
 *
 * Returns:
 * - role: null | 'admin' | 'super_admin'
 * - isAdmin: boolean (any admin role)
 * - isSuperAdmin: boolean (super_admin only)
 * - isLoading: boolean
 * - refresh: () => void — force re-check (e.g. after role change)
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '@deskbay/shared/services/supabase/client';
import { logger } from '@deskbay/shared';

type AdminRole = 'admin' | 'super_admin' | null;

interface AdminRoleState {
    role: AdminRole;
    isAdmin: boolean;
    isSuperAdmin: boolean;
    isLoading: boolean;
    refresh: () => void;
}

// Session-level cache — shared across all hook instances
let cachedRole: AdminRole | undefined = undefined;
let cachePromise: Promise<AdminRole> | null = null;

// Reset cache on logout (called from anywhere)
export function resetAdminRoleCache(): void {
    cachedRole = undefined;
    cachePromise = null;
}

async function fetchAdminRole(): Promise<AdminRole> {
    try {
        // Guard: skip RPC if no active session to avoid 401 before auth is ready
        const { data: { session } } = await supabase.auth.getSession();
        if (!session?.user?.id) return null;

        const { data, error } = await supabase.rpc('get_admin_role');
        if (error) {
            // 42501 = permission denied (non-authenticated) — treat as non-admin
            if ((error as { code?: string })?.code === '42501') {
                return null;
            }
            logger.auth.error('get_admin_role RPC error', error, 'useAdminRole');
            return null;
        }
        return (data as AdminRole) ?? null;
    } catch (error: unknown) {
        logger.auth.error('get_admin_role fetch failed', error, 'useAdminRole');
        return null;
    }
}

export function useAdminRole(): AdminRoleState {
    const [role, setRole] = useState<AdminRole>(cachedRole ?? null);
    const [isLoading, setIsLoading] = useState(cachedRole === undefined);
    const mountedRef = useRef(true);

    const loadRole = useCallback(async (force = false) => {
        // If cached and not forcing, use cache
        if (!force && cachedRole !== undefined) {
            setRole(cachedRole);
            setIsLoading(false);
            return;
        }

        setIsLoading(true);

        // Deduplicate concurrent calls
        if (!cachePromise || force) {
            cachePromise = fetchAdminRole();
        }

        const result = await cachePromise;
        cachedRole = result;

        if (mountedRef.current) {
            setRole(result);
            setIsLoading(false);
        }
    }, []);

    useEffect(() => {
        mountedRef.current = true;
        loadRole();
        return () => { mountedRef.current = false; };
    }, [loadRole]);

    const refresh = useCallback(() => {
        cachePromise = null;
        loadRole(true);
    }, [loadRole]);

    return {
        role,
        isAdmin: role === 'admin' || role === 'super_admin',
        isSuperAdmin: role === 'super_admin',
        isLoading,
        refresh,
    };
}
